import { useEffect, useState } from "react";
import DetailsDialog, { DetailsProps } from "../DetailsDialog";
import NumberInputField from "../ui/NumberInputField";
import TextInputField from "../ui/TextInputField";
import PocketSpringZoneDeclaration from "./PocketSpringZoneDeclaration";
import PocketSpringZoneDefinition from "./PocketSpringZoneDefinition";
import { PocketSpring, PocketSpringZoneModel } from "../../api/OrderSpringApi";
import useSpringMaterialApi, { SpringMaterial } from "../../api/SpringMaterialApi";
import { SpringMaterialCustomerPrice } from "../../api/SpringMaterialCustomerPriceApi";
import { BigNumber } from "bignumber.js";

interface Properties
extends DetailsProps {
  readonly customerId: number;
  readonly priceListId: number;
  readonly materialPrices: SpringMaterialCustomerPrice[];
}

const PocketSpringDetails = (properties: Properties) => {
  const springMaterialApi = useSpringMaterialApi();
  const customerId = properties.customerId;
  const materialPrices = properties.materialPrices;
  const [materials, setMaterials] = useState<SpringMaterial[]>();
  const [name, setName] = useState<string>();
  const [width, setWidth] = useState<number>();
  const [zones, setZones] = useState<PocketSpringZoneModel[]>();
  const [price, setPrice] = useState<number|null>();

  const initializeFields = (data: PocketSpring) => {
    setName(data.name);
    setWidth(data.width);
    setPrice(data.price);
    setZones(data.zones.map(zone => ({
      material: zone.material,
      length: zone.length
    })));
  };

  const collectData = () =>
    (name && width)
    ? {
      name,
      width,
      zones: zones || []
    }
    : null;

  const fetchMaterials = () => {
    springMaterialApi
      .fetchPocketSpringMaterials()
      .then(materials => setMaterials(materials))
      .catch(reason => console.error(`Could not fetch materials: ${reason}`));
  };

  const isNotGreaterThanZero = (value: number|null|undefined) =>
    value !== undefined && value !== null && value <= 0;

  const addZone = (zone: PocketSpringZoneModel) => {
    if (zones) {
      setZones([ ...zones, zone]);
    } else {
      setZones([ zone ]);
    }
  };

  const deleteZone = (removedZone: PocketSpringZoneModel) => {
    const updatedZones = zones?.filter(zone => zone !== removedZone);
    setZones(updatedZones);
  };

  const setZoneLength = (updatedZone: PocketSpringZoneModel, length: number) => {
    const updatedZones = zones?.map(zone => zone === updatedZone ? { ... updatedZone, length } : zone);
    setZones(updatedZones);
  };

  useEffect(() => {
    if (width && zones) {
      const zoneMaterialPriceNotDefinedException = 'Zone material price not defined';
      let total = new BigNumber(0);
      let zone: PocketSpringZoneModel;
      try {
        for (zone of zones) {
          const zoneMaterialPrice = materialPrices[zone.material.id];
          if (zoneMaterialPrice === undefined) {
            throw zoneMaterialPriceNotDefinedException;
          }
          const zonePrice = new BigNumber(zoneMaterialPrice.unitPrice).times(width).times(zone.length);
          total = total.plus(zonePrice);
        }
      } catch (exception) {
        if (exception === zoneMaterialPriceNotDefinedException) {
          total = new BigNumber(0);
        }
      }
      setPrice(Number(total.toFixed(2)));
    }
  }, [width, zones, materialPrices]);

  useEffect(() => {
    fetchMaterials();
  }, [customerId]);

  const renderFields = (initialData: PocketSpring|undefined) => (
    <>
      <h2>Wprowadź dane zestawu sprężyn kieszeniowych</h2>
      <TextInputField
        label='Nazwa'
        autoFocus
        initialValue={initialData?.name}
        errorMessage={name => !name ? "Nazwa jest wymagana" : null}
        onValueChange={setName}
      />
      <NumberInputField
        label='Szerokość'
        initialValue={initialData?.width}
        onValueChange={setWidth}
      />
      <NumberInputField
        label='Cena [zł]'
        initialValue={initialData?.price}
        calculatedValue={price}
        errorMessage={() => isNotGreaterThanZero(price) ? "Wyliczona cena jest zbyt niska, wybierz inne materiały lub zwiększ wymiary" : null}
        disabled
      />
      { materials && <PocketSpringZoneDeclaration
          materials={ materials }
          addZone={ addZone }
          materialPrices={ materialPrices }
        />
      }
      <div>
        { zones && zones.map(zone => 
          <PocketSpringZoneDefinition
            zone={ zone }
            setLength={ length => setZoneLength(zone, length) }
            deleteZone={ () => deleteZone(zone) }
            materialPrices={ materialPrices }
          />
        )}
      </div>
    </>
  );

  return <>
    <DetailsDialog<PocketSpring>
      { ...properties }
      resource={`customers/${customerId}/pocket-springs`}
      renderFields={renderFields}
      initializeFields={initializeFields}
      collectData={collectData}
    />
  </>;
};
export default PocketSpringDetails;